import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useClinic } from '@/contexts/ClinicContext';
import { Label } from '@/components/ui/label';
import { Mail, Shield, Eye, EyeOff } from 'lucide-react';
import { getFooterText } from '@/config/version';

const loginSchema = z.object({
  email: z.string().nonempty('Please enter email.').email('Please enter a valid email.'),
  password: z.string().nonempty('Please enter password.'),
});

const forgotPasswordSchema = z.object({
  email: z.string().nonempty('Please enter email.').email('Please enter a valid email.'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const Login = () => {
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const forgotForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, forgotPassword } = useAuth();
  const { getClinic } = useClinic();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || '/';

  const handleForgotSubmit = async (data: LoginFormData) => {
    setIsForgotPasswordLoading(true);
    try {
      await forgotPassword(data?.email);
      toast({
        title: 'Success',
        description: 'Password reset email sent!',
        variant: 'success',
      });
      setIsForgotPasswordOpen(false);
    } catch (error) {
      const errorMessage = error?.message ?? 'Failed to send reset email. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const user = await login(data.email, data.password);

      const hasOrgAccess = Array.isArray(user?.organizationMemberships) && user.organizationMemberships.length > 0;
      const hasMultipleClinics = Boolean(user?.hasMultipleClinics && user?.clinics && user.clinics.length > 1);

      if (hasOrgAccess || hasMultipleClinics) {
        navigate('/clinic-selection', { replace: true });
        return;
      }

      const clinic = await getClinic(user?.clinic?.id);
      toast({
        title: 'Success',
        description: 'Successfully signed in!',
        variant: 'success',
      });

      if (clinic?.is_admin) {
        navigate('/', { replace: true });
      } else if (clinic?.is_practitioner) {
        navigate('/practitioner-portal', { replace: true });
      } else if (clinic?.is_clinic_staff) {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Error',
        description: 'Invalid email or password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branded Visual */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 relative bg-[#22201E] flex-col justify-between p-10 overflow-hidden">
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.5" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        {/* Soft radial glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-white/[0.02] rounded-full blur-3xl" />

        {/* Top - Logo */}
        <div className="relative z-10">
          <img
            src="/nimbus_logo_white.svg"
            alt="Nimbus Healthcare"
            className="h-9"
          />
        </div>

        {/* Center - Headline */}
        <div className="relative z-10 space-y-6">
          <div>
            <h2 className="text-[42px] font-normal text-[#FAF9F6] leading-[1.15] tracking-tight font-fraunces">
              Transform Your
              <br />
              Practice with
              <br />
              <span className="text-[#C37657]">Nimbus Providers.</span>
            </h2>
            <p className="text-[14px] text-white/50 mt-4 leading-relaxed max-w-[340px]">
              Simplify patient management, streamline payments, and elevate your private-pay healthcare experience.
            </p>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-6 pt-2">
            <div>
              <p className="text-[24px] font-bold text-[#FAF9F6]">HIPAA</p>
              <p className="text-[11px] text-white/40 mt-0.5">Fully compliant</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-[24px] font-bold text-[#FAF9F6]">E-Rx</p>
              <p className="text-[11px] text-white/40 mt-0.5">E-Prescriptions</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-[24px] font-bold text-[#FAF9F6]">24/7</p>
              <p className="text-[11px] text-white/40 mt-0.5">Patient access</p>
            </div>
          </div>

          {/* Feature list */}
          <div className="space-y-3 pt-2">
            {['Patient management & scheduling', 'Secure payments & invoicing', 'E-prescriptions & clinical workflows'].map((feature) => (
              <div key={feature} className="flex items-center gap-2.5">
                <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />
                <p className="text-[12px] text-white/50">{feature}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom - Copyright */}
        <div className="relative z-10">
          <p className="text-[11px] text-white/25">
            &copy; {new Date().getFullYear()} Nimbus Healthcare. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:flex-1 flex flex-col bg-[#FAFAF8]" style={{ minHeight: '100vh' }}>
        {/* Form Container */}
        <div className="flex items-center justify-center px-4 py-8 lg:p-8 flex-1">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <img
                src="/nimbus_logo.svg"
                alt="Nimbus Healthcare"
                className="h-10"
              />
              <div className="mt-3 flex items-center">
                <div className="h-px w-8 bg-slate-300" />
                <span className="mx-3 text-xs font-semibold text-brand-teal uppercase tracking-widest">
                  Clinic & Practitioner
                </span>
                <div className="h-px w-8 bg-slate-300" />
              </div>
            </div>

            {/* Form Card */}
            <motion.div
              className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {/* Header inside card */}
              <div className="mb-8">
                <h1 className="text-xl font-semibold text-gray-900 mb-1">
                  Welcome back
                </h1>
                <p className="text-sm text-gray-500">
                  Please sign in to your account
                </p>
              </div>

              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email address
                  </label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    {...form.register('email')}
                    className="h-12 rounded-xl border-gray-200 focus:border-brand-teal focus:ring-brand-teal/20 text-base placeholder:text-gray-400 transition-all"
                  />
                  {form.formState.errors.email && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      {...form.register('password')}
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-teal focus:ring-brand-teal/20 text-base placeholder:text-gray-400 transition-all pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {form.formState.errors.password && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.password.message}</p>
                  )}
                </div>

                {/* Forgot Password Link */}
                <div className="flex justify-end">
                  <Dialog open={isForgotPasswordOpen} onOpenChange={setIsForgotPasswordOpen}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="text-sm text-brand-teal hover:text-brand-teal/80 font-medium transition-colors"
                      >
                        Forgot password?
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px] rounded-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-xl">Reset Password</DialogTitle>
                        <DialogDescription className="text-sm font-inter text-gray-500">
                          Enter your email address and we'll send you a link to reset your password.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={forgotForm.handleSubmit(handleForgotSubmit)}>
                        <div className="grid gap-4 py-4 mb-2">
                          <div className="space-y-2">
                            <Label htmlFor="forgot-email" className="text-gray-700 text-sm">
                              Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id="forgot-email"
                              type="email"
                              placeholder="Enter your email address"
                              {...forgotForm.register('email')}
                              className="h-12 rounded-xl border-gray-200 focus:border-brand-teal focus:ring-brand-teal/20"
                            />
                            {forgotForm.formState.errors.email && (
                              <p className="text-sm text-red-500">
                                {forgotForm.formState.errors.email.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            type="button"
                            disabled={isForgotPasswordLoading}
                            className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white"
                            onClick={forgotForm.handleSubmit(handleForgotSubmit)}
                          >
                            {isForgotPasswordLoading ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>Sending...</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <Mail className="h-4 w-4" />
                                <span>Send Reset Link</span>
                              </div>
                            )}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white text-base font-semibold shadow-lg shadow-brand-teal/25 hover:shadow-xl hover:shadow-brand-teal/30 transition-all duration-300"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Security Note */}
            <motion.div
              className="mt-8 flex items-center justify-center gap-2 text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Shield className="w-4 h-4" />
              <span>Secured with enterprise-grade encryption</span>
            </motion.div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center text-xs text-gray-400 mt-auto">
          <p>{getFooterText()}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
